﻿import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../hooks/useCart";
import { useCurrency } from "../../context/CurrencyContext";
import { getProductBySlug } from "../../api/woocommerce";

export default function ProductCard({ product }) {
    const { addToCart } = useCart();
    const { formatPrice } = useCurrency();

    const [modalOpen, setModalOpen] = useState(false);
    const [fullProduct, setFullProduct] = useState(null);
    const [loadingVariations, setLoadingVariations] = useState(false);
    const [selectedAttributes, setSelectedAttributes] = useState({});
    const [selectedVariation, setSelectedVariation] = useState(null);
    const [error, setError] = useState("");
    const [addedToCart, setAddedToCart] = useState(false);

    const handleAddToCart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (product.type === "variable") {
            setModalOpen(true);
        } else {
            addToCart(product, 1);
            setAddedToCart(true);
            setTimeout(() => setAddedToCart(false), 2000);
        }
    };

    useEffect(() => {
        if (!modalOpen) return;
        if (fullProduct) return;
        setLoadingVariations(true);
        setSelectedAttributes({});
        setSelectedVariation(null);
        setError("");
        getProductBySlug(product.slug)
            .then((data) => setFullProduct(data))
            .catch(() => setError("Failed to load options."))
            .finally(() => setLoadingVariations(false));
    }, [modalOpen, product.slug]);

    const matchVariation = useCallback((attrs) => {
        if (!fullProduct?.variations?.length) return null;
        return fullProduct.variations.find((v) =>
            v.attributes.every((a) => attrs[a.key] === a.slug)
        ) || null;
    }, [fullProduct]);

    const handleAttributeChange = (attrKey, slug) => {
        const newAttrs = { ...selectedAttributes, [attrKey]: slug };
        setSelectedAttributes(newAttrs);
        const allSelected = fullProduct.attributes.every((a) => newAttrs[a.key]);
        if (allSelected) {
            const matched = matchVariation(newAttrs);
            setSelectedVariation(matched);
            if (!matched) setError("This combination is not available.");
            else setError("");
        } else {
            setSelectedVariation(null);
            setError("");
        }
    };

    const handleModalAdd = () => {
        const allSelected = fullProduct.attributes.every((a) => selectedAttributes[a.key]);
        if (!allSelected) { setError("Please select all options."); return; }
        if (!selectedVariation) { setError("This combination is not available."); return; }
        addToCart(product, 1, selectedVariation);
        setAddedToCart(true);
        setTimeout(() => { setAddedToCart(false); setModalOpen(false); }, 1500);
    };

    const hasDiscount = product.on_sale && product.regular_price && product.sale_price;

    return (
        <>
            <Link to={"/shop/" + product.slug} className="group card flex flex-col">
                <div className="relative aspect-square bg-neutral-100 overflow-hidden">
                    {product.image ? (
                        <img src={product.image} alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-neutral-300">
                            <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                    )}
                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                        {product.on_sale && <span className="badge badge-danger text-[10px]">Sale</span>}
                        {product.featured && <span className="badge badge-primary text-[10px]">Featured</span>}
                        {!product.in_stock && <span className="badge bg-neutral-200 text-neutral-600 text-[10px]">Out of Stock</span>}
                    </div>
                    {product.in_stock && (
                        <button onClick={handleAddToCart}
                            className="absolute bottom-2 left-2 right-2 bg-primary-600 text-white text-xs font-semibold py-2.5 rounded-lg md:opacity-0 md:group-hover:opacity-100 md:translate-y-2 md:group-hover:translate-y-0 transition-all duration-200 hover:bg-primary-700 cursor-pointer">
                            {product.type === "variable" ? "Select Options" : "Add to Cart"}
                        </button>
                    )}
                </div>
                <div className="p-3 flex flex-col flex-1">
                    {product.categories?.[0] && (
                        <span className="text-[11px] text-neutral-400 uppercase tracking-wide mb-1">{product.categories[0].name}</span>
                    )}
                    <h3 className="text-sm font-semibold text-neutral-800 leading-snug mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors flex-1">{product.name}</h3>
                    {product.rating > 0 && (
                        <div className="flex items-center gap-1 mb-2">
                            <StarRating rating={product.rating} />
                            <span className="text-[11px] text-neutral-400">({product.review_count})</span>
                        </div>
                    )}
                    <div className="flex items-center gap-2 mt-auto">
                        <span className="text-base font-bold text-primary-600">{formatPrice(product.price || 0)}</span>
                        {hasDiscount && <span className="text-xs text-neutral-400 line-through">{formatPrice(product.regular_price)}</span>}
                    </div>
                </div>
            </Link>

            {modalOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}>
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in">
                        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
                            <div className="flex items-center gap-3 min-w-0">
                                {product.image && (
                                    <img src={selectedVariation?.image || product.image} alt=""
                                        className="w-12 h-12 rounded-lg object-cover shrink-0" />
                                )}
                                <div className="min-w-0">
                                    <h3 className="text-sm font-bold text-neutral-800 truncate">{product.name}</h3>
                                    <p className="text-sm font-bold text-primary-600 mt-0.5">
                                        {formatPrice(selectedVariation?.price ?? (product.price || 0))}
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => setModalOpen(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-all shrink-0 cursor-pointer"
                                aria-label="Close">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-5">
                            {loadingVariations ? (
                                <div className="flex items-center justify-center py-10">
                                    <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                                </div>
                            ) : fullProduct?.attributes ? (
                                fullProduct.attributes.map((attr) => (
                                    <div key={attr.key} className="mb-5">
                                        <label className="block text-xs font-semibold text-neutral-700 mb-2 uppercase tracking-wide">
                                            {attr.name}
                                            {selectedAttributes[attr.key] && (
                                                <span className="ml-1 text-primary-600 normal-case font-semibold">
                                                    {" "}—{" "}
                                                    {attr.options.find((o) => o.slug === selectedAttributes[attr.key])?.label}
                                                </span>
                                            )}
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {attr.options.map((opt) => {
                                                const isSelected = selectedAttributes[attr.key] === opt.slug;
                                                return (
                                                    <button key={opt.slug}
                                                        onClick={() => handleAttributeChange(attr.key, opt.slug)}
                                                        className={"px-3 py-1.5 rounded-lg border text-sm font-medium cursor-pointer transition-all " +
                                                            (isSelected ? "border-primary-600 bg-primary-600 text-white" : "border-neutral-300 text-neutral-700 hover:border-primary-400")}>
                                                        {opt.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-red-500 py-4">{error || "Failed to load product options."}</p>
                            )}
                            {error && !loadingVariations && <p className="text-sm text-red-500 mb-3">{error}</p>}
                            {addedToCart && (
                                <div className="flex items-center gap-2 text-sm text-green-600 font-medium mb-3">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                    Added to Cart!
                                </div>
                            )}
                        </div>
                        <div className="p-5 pt-0">
                            <button onClick={handleModalAdd}
                                disabled={loadingVariations || !selectedVariation || addedToCart}
                                className="w-full py-3 rounded-xl bg-primary-600 text-white font-semibold text-sm hover:bg-primary-700 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                {loadingVariations ? (
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : addedToCart ? "Added!" : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.5 6h13M7 13L5.4 5M10 21a1 1 0 100-2 1 1 0 000 2zm7 0a1 1 0 100-2 1 1 0 000 2z" />
                                        </svg>
                                        Add to Cart
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function StarRating({ rating }) {
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
                <svg key={star} className={"w-3 h-3 " + (star <= Math.round(rating) ? "text-yellow-400" : "text-neutral-200")}
                    viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
            ))}
        </div>
    );
}